import { cva, type VariantProps } from "cva";

/**
 * SegmentedControl Component Styles
 *
 * Sizes mirror Button: track height and outer radius match the corresponding
 * Button size. Inner trigger radius is outer radius minus the 4px track
 * padding so the indicator visually nests inside the track.
 *
 * xs: track h-32 radius 12 → trigger h-24 radius 8
 * s:  track h-40 radius 14 → trigger h-32 radius 10
 * m:  track h-48 radius 16 → trigger h-40 radius 12
 * l:  track h-56 radius 20 → trigger h-48 radius 16
 */
export const segmentedControlListStyles = cva({
  base: "inline-flex items-center p-4 bg-ink-100 gap-x-4 font-ginto",
  variants: {
    size: {
      xs: "rounded-xs",
      s: "rounded-sm",
      m: "rounded-default",
      l: "rounded-md",
    },
  },
  defaultVariants: {
    size: "m",
  },
});

export const segmentedControlTriggerStyles = cva({
  base: [
    "relative inline-flex items-center justify-center cursor-pointer font-medium",
    "bg-transparent text-content-primary/60 hover:text-content-primary transition-colors duration-150 ease-out-quad",
    "data-[state=active]:text-content-primary",
    "outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border",
  ],
  variants: {
    size: {
      xs: "px-12 text-ginto-14 h-24 rounded-[8px]",
      s: "px-16 text-ginto-14 h-32 rounded-[10px]",
      m: "px-24 text-ginto-16 h-40 rounded-xs",
      l: "px-32 text-ginto-18 h-48 rounded-default",
    },
  },
  defaultVariants: {
    size: "m",
  },
});

export const segmentedControlIndicatorStyles = cva({
  base: "absolute inset-0 bg-surface shadow-sm",
  variants: {
    size: {
      xs: "rounded-[8px]",
      s: "rounded-[10px]",
      m: "rounded-xs",
      l: "rounded-default",
    },
  },
  defaultVariants: {
    size: "m",
  },
});

export type SegmentedControlStylesProps = VariantProps<typeof segmentedControlListStyles>;
